import axios from "axios";
import router from "../router";

import { account } from "@/urls";
import { getProfile } from "../shared/security/authority";

export default {
  namespaced: true,
  state: {
    logon: false,
    userIdentity: null,
    authenticated: false,
    authenticationError: false
  },
  getters: {
    logon: state => state.logon,
    account: state => state.userIdentity,
    authenticated: state => state.authenticated,
    activeProfiles: state => state.activeProfiles,
    authenticationError: state => state.authenticationError,
    username: state => (state.userIdentity ? state.userIdentity.login : ""),
    userAuthorities: state =>
      state.userIdentity ? state.userIdentity.authorities : null,
    activeProfile: state => {
      if (state.userIdentity) {
        return getProfile(state.userIdentity.authorities);
      }
      return null;
    }
  },
  mutations: {
    authenticate(state) {
      state.logon = true;
    },
    authenticated(state, identity) {
      state.userIdentity = identity;
      state.authenticated = true;
      state.logon = false;
    },
    logout(state) {
      state.userIdentity = null;
      state.authenticated = false;
      state.logon = false;
    },
    setActiveProfiles(state, profile) {
      state.activeProfiles = profile;
    },
    SET_AUTHENTICATION_ERROR(state, authenticationError) {
      state.authenticationError = authenticationError;
    }
  },
  actions: {
    async sendActivationEmail({ dispatch }, payload) {
      try {
        await axios.get(account.ACTIVATION_EMAIL_URL, { params: { idUser: payload } });
        dispatch("snackbar/openSnackbar", "E-mail inviata con successo", {
          root: true
        });
      } catch (err) {
        console.log(err);
        dispatch("snackbar/openErrorSnackbar", err, { root: true });
      }
    },
    doLogin({ commit, dispatch }, payload) {
      const data = {
        username: payload.username,
        password: payload.password,
        rememberMe: payload.rememberMe
      };
      axios
        .post(account.AUTHENTICATE_URL, data)
        .then(result => {
          const bearerToken = result.headers.authorization;
          if (bearerToken && bearerToken.slice(0, 7) === "Bearer ") {
            const jwt = bearerToken.slice(7, bearerToken.length);
            if (payload.rememberMe) {
              localStorage.setItem("jhi-authenticationToken", jwt);
            } else {
              sessionStorage.setItem("jhi-authenticationToken", jwt);
            }
          }
          commit("SET_AUTHENTICATION_ERROR", false);
          dispatch("retrieveAccount");
        })
        .catch(() => {
          commit("SET_AUTHENTICATION_ERROR", true);
        });
    },
    doLogout({ commit }) {
      localStorage.removeItem("jhi-authenticationToken");
      sessionStorage.removeItem("jhi-authenticationToken");
      sessionStorage.removeItem("requested-url");
      commit("logout");
      router.push("/");
    },
    retrieveAccount({ commit, dispatch }) {
      return new Promise(resolve => {
        axios
          .get(account.ACCOUNT_URL)
          .then(response => {
            commit("authenticate");
            const account = response.data;
            if (account) {
              commit("authenticated", account);
              if (sessionStorage.getItem("requested-url")) {
                router.replace(sessionStorage.getItem("requested-url"));
                sessionStorage.removeItem("requested-url");
              } else {
                router.push("/home");
              }
            } else {
              dispatch("doLogout");
            }
            resolve(true);
          })
          .catch(() => {
            dispatch("doLogout");
          });
      });
    },
    async hasAnyAuthorityAndCheckAuth(
      { dispatch, getters, commit },
      authorities
    ) {
      if (typeof authorities === "string") {
        authorities = [authorities];
      }
      if (!getters.authenticated || !getters.userAuthorities) {
        const token =
          localStorage.getItem("jhi-authenticationToken") ||
          sessionStorage.getItem("jhi-authenticationToken");
        if (!getters.account && !getters.logon && token) {
          if (token) {
            try {
              const { data } = await axios.get(account.ACCOUNT_URL);
              if (data) {
                commit("authenticated", data);
              }
            } catch (err) {
              return dispatch("doLogout");
            }
          } else {
            return dispatch("retrieveAccount");
          }
        } else {
          return new Promise(resolve => {
            resolve(false);
          });
        }
      }

      for (let i = 0; i < authorities.length; i++) {
        if (getters.userAuthorities.includes(authorities[i])) {
          return new Promise(resolve => {
            resolve(true);
          });
        }
      }

      return new Promise(resolve => {
        resolve(false);
      });
    },
    async resetPasswordInit({ dispatch }, email) {
      try {
        await axios.post(account.RESET_PASSWORD_INIT_URL, email, {
          headers: {
            "content-type": "text/plain"
          }
        });
        dispatch(
          "snackbar/openSnackbar",
          "E' stata inviata una e-mail per il reset",
          {
            root: true
          }
        );
      } catch (err) {
        console.log(err);
        dispatch("snackbar/openErrorSnackbar", err, { root: true });
      }
    },
    async resetPasswordFinish(_, payload) {
      try {
        await axios.post(account.RESET_PASSWORD_FINISH_URL, payload);
        return new Promise(resolve => {
          resolve({ success: true });
        });
      } catch (err) {
        const status = err.response.data.status;
        const title = err.response.data.title;
        const detail = err.response.data.detail;
        return new Promise(resolve => {
          resolve({ success: false, status, title, detail });
        });
      }
    },
    async activateAccount(_, payload) {
      try {
        await axios.post(account.ACCOUNT_ACTIVATE_URL, payload);
        return new Promise(resolve => {
          resolve({ success: true });
        });
      } catch (err) {
        const status = err.response.data.status;
        const title = err.response.data.title;
        const detail = err.response.data.detail;
        return new Promise(resolve => {
          resolve({ success: false, status, title, detail });
        });
      }
    },
    async privacyActivate(_,  payload) {
      const params = {
        key: payload.key,
      };
      try {
        const { data } = await axios.get(account.PRIVACY_ACTIVATE_URL, { params });
        return new Promise(resolve => {
          resolve({ success: true, data });
        });
      } catch (err) {
        const status = err.response.status;
        return new Promise(resolve => {
          resolve({ success: false, status });
        });
      }
    },
    async privacyPage(_, payload) {
      const params = {
        cfazienda: payload.cfazienda,
        Codicecliente: payload.Codicecliente,
        Codicedistribuzione: payload.Codicedistribuzione,
        NumeroProdotto: payload.NumeroProdotto,
      };
      try {
        const { data } = await axios.get(account.PRIVACY_PAGE_URL, { params });
        return new Promise(resolve => {
          resolve({ success: true, html: data });
        });
      } catch (err) {
        const status = err.response.status;
        return new Promise(resolve => {
          resolve({ success: false, status });
        });
      }
    },
    async privacy() {
      try {
        const { data } = await axios.get(account.PRIVACY_URL);
        return new Promise(resolve => {
          resolve({ success: true, html: data });
        });
      } catch (err) {
        const status = err.response.status;
        return new Promise(resolve => {
          resolve({ success: false, status });
        });
      }
    },
    async fetchUserData({ dispatch }) {
      try {
        const { data } = await axios.get(account.USERDATA_URL);
        return new Promise(resolve => {
          resolve(data);
        });
      } catch (err) {
        dispatch("snackbar/openBeExceptionSnackbar", err, { root: true });
      }
    },
    async updateUserData({ dispatch }, payload) {
      try {
        await axios.post(`${account.USERDATA_URL}`, { login: payload.login, email: payload.email });
        dispatch("snackbar/openSnackbar", "I dati sono stati salvati. Se hai modificato lo username devi rieffettuare il login", { root: true });
      } catch (err) {
        dispatch("snackbar/openBeExceptionSnackbar", err, { root: true });
      }
    },
  }
};
